import { Box, Button, ButtonProps, Card, CardProps, Stack, StackProps, Typography } from '@mui/material';
import { ElementType, PropsWithChildren } from 'react';

import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { cardBgLightGrey } from '@/theme';

export interface SummaryCardProps extends CardProps {
    title?: string;
    isLoading?: boolean;
    children: React.ReactNode;
}

export const SummaryCard = ({ title, isLoading, children, sx, ...props }: SummaryCardProps) => {
    return (
        <Card
            sx={{
                p: 4,
                bgcolor: cardBgLightGrey,
                flex: 1,
                borderRadius: 0,
                gap: 1,
                display: 'flex',
                flexDirection: 'column',
                ...sx,
            }}
            elevation={0}
            {...props}
        >
            {title && <Typography variant="h5">{title}</Typography>}
            {isLoading && <GenericSkeleton lines={1} />}
            <Box sx={{ display: isLoading ? 'none' : 'contents' }}>{children}</Box>
        </Card>
    );
};

export const SummaryCardBody = ({ children }: PropsWithChildren) => <Typography component="div">{children}</Typography>;

export const SummaryCardSubtext = ({ children }: PropsWithChildren) => (
    <Typography
        component="div"
        variant="minorLabel"
        sx={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
        }}
    >
        {children}
    </Typography>
);

export const SummaryCardButton = <C extends ElementType>({
    children,
    ...buttonProps
}: PropsWithChildren & ButtonProps<C, { component?: C }>) => (
    <Button
        variant="text"
        size="small"
        sx={{
            left: '-5px',
            py: 0.5,
            fontSize: '0.875rem',
            position: 'relative',
            minWidth: 'unset',
            alignSelf: 'start',
        }}
        {...buttonProps}
    >
        {children}
    </Button>
);

export const SummaryCardRow = ({ sx, ...props }: StackProps) => (
    <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ alignItems: 'stretch', gap: { xs: 1, sm: 3 }, ...sx }}
        {...props}
    />
);

export const ConjoinedSummaryCards = ({ sx, ...props }: StackProps) => (
    <SummaryCardRow
        sx={{
            gap: 0,
            '& > div': {
                py: { xs: 0.5, sm: 3 },
            },
            '& > div:first-of-type': { pt: 3 },
            '& > div:last-of-type': { pb: 3 },
            ...sx,
        }}
        {...props}
    />
);
